import React from 'react';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import Grid from '@material-ui/core/Grid';
import Grow from '@material-ui/core/Grow';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import {makeStyles, Theme} from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import FormikDatePicker from '../common/FormikDatePicker';
import {ErrorMessage, FieldArray, Form, Formik} from 'formik';
import {AdRequestProffer, DigiknowLocation} from '../../ext/lib/types';
import {stripISOTimeComponent} from '../../ext/lib/temporal';
import {isValidCcoaNum} from '../../ext/common/accounting';
import {parseISO, differenceInCalendarDays} from 'date-fns/fp';
import * as R from 'ramda';
import * as Yup from 'yup';

const useStyles = makeStyles((theme: Theme) => ({
  listItem: {
    padding: theme.spacing(1, 0)
  },
  title: {
    marginTop: theme.spacing(2)
  },
  formControl: {
    margin: theme.spacing(2)
  },
  group: {
    margin: theme.spacing(1, 0)
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1)
  }
}));

function isRechargableOrg(orgType: string): boolean {
  return ['Campus Department', 'Campus Department w/ Sponsorship Logo'].includes(orgType);
}

const AdDetailsForm: React.FC<AdDetailsFormProps> = ({setAdDetails, proffer}) => {
  const classes = useStyles();

  //#region Locations

  function handleSubmit(values: any, {setSubmitting}: { setSubmitting: (_: boolean) => void }) {
    const adRequest = {
      adName: values.adName,
      contactPhoneNum: values.phoneNum,
      comments: values.comments,
      organization: values.organization,
      organizationType: values.orgType,
      rechargeNumber: values.rechargeNum,
      billingPlan: values.billingPlan,
      slideCount: values.slideCount,
      locations: values.locations,
      // remove unneccessary time components which were
      // used to work around DatePicker component issues.
      firstDate: stripISOTimeComponent(values.firstDate),
      lastDate: stripISOTimeComponent(values.lastDate)
    };
    setAdDetails(adRequest);
    setSubmitting(false);
  }

  return (
    <Formik
      initialValues={{
        organization: proffer.organization || '',
        orgType: proffer.organizationType || '',
        rechargeNum: proffer.rechargeNumber || '',
        adName: proffer.adName || '',
        billingPlan: proffer.billingPlan || '',
        phoneNum: proffer.contactPhoneNum || '',
        firstDate: proffer.firstDate || '',
        lastDate: proffer.lastDate || '',
        locations: proffer.locations || [],
        slideCount: proffer.slideCount || '',
        comments: proffer.comments || ''
      }}
      validate={
        values => {
          type SubmittedDates = {firstDate: string; lastDate: string};

          function validateRechargeNum(orgType: string, rechargeNum: string): Record<string, string> {
            const isRechargeOrg = isRechargableOrg(orgType);
            const isValid = isValidCcoaNum(rechargeNum);
            return isRechargeOrg && !isValid
              ? {rechargeNum: 'Recharge number is not valid'}
              : {};
          }

          function validateRunDates({firstDate, lastDate}: SubmittedDates): Record<string, string> {
            const first = parseISO(firstDate);
            const last = parseISO(lastDate);
            const diff = differenceInCalendarDays(first, last);
            if (diff < 0) {
              return {lastDate: 'Last day must not be sooner than First day'};
            } else if (diff > 6) {
              return {lastDate: 'Ad cannot be run for more than 7 days'};
            } else {
              return {};
            }
          }

          return R.merge(validateRechargeNum(values.orgType, values.rechargeNum), validateRunDates(values));
        }
      }
      validationSchema={Yup.object().shape({
        organization: Yup.string().max(500, 'Organization name must be 500 characters or less').required('Organization name is required'),
        orgType: Yup.string().required('Select an organization type'),
        adName: Yup.string().max(500, 'Ad name must be 500 characters or less').required('Ad name is required'),
        billingPlan: Yup.string().required('Select a billing plan'),
        phoneNum: Yup.string().max(20, 'Phone number must be 20 characters or less').required('Phone number is required'),
        firstDate: Yup.date().required('First day is required'),
        lastDate: Yup.date().required('Last day is required'),
        locations: Yup.array().of(Yup.string()).min(1, 'Select at least one location'),
        slideCount: Yup.number().min(1).max(255, 'Slide count must be less than 256'),
        rechargeNum: Yup.string(),
        comments: Yup.string().max(2500, 'Comments must be 2500 characters or less')
      })}
      onSubmit={handleSubmit}
    >
      {({handleChange, handleBlur, values, errors, touched}) =>
        <Form>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth className={classes.formControl}>
                <TextField
                  name="organization"
                  value={values.organization}
                  label="Organization name"
                  error={Boolean(errors.organization) && touched.organization}
                  variant="outlined"
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <ErrorMessage name="organization">
                  {(msg: any) => <FormHelperText error>{msg}</FormHelperText>}
                </ErrorMessage>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth className={classes.formControl}>
                <TextField
                  name="phoneNum"
                  value={values.phoneNum}
                  label="Phone"
                  error={Boolean(errors.phoneNum) && touched.phoneNum}
                  type="tel"
                  autoComplete="tel-national"
                  variant="outlined"
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <ErrorMessage name="phoneNum">
                  {(msg: any) => <FormHelperText error>{msg}</FormHelperText>}
                </ErrorMessage>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormLabel
                  error={Boolean(errors.orgType) && touched.orgType}
                  component="legend"
                >Organization type</FormLabel>
                <ErrorMessage name="orgType">
                  {(msg: any) => <FormHelperText error>{msg}</FormHelperText>}
                </ErrorMessage>
                <RadioGroup
                  className={classes.group}
                  aria-label="orgType"
                  name="orgType"
                  value={values.orgType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <FormControlLabel
                    control={<Radio />}
                    label="Campus Department"
                    value="Campus Department"
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label="Campus Department w/ Sponsorship Logo"
                    value="Campus Department w/ Sponsorship Logo"
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label="Housing/Residential Dining"
                    value="Housing/Residential Dining"
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label="Registered Student Group"
                    value="Registered Student Group"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormLabel
                  error={Boolean(errors.billingPlan) && touched.billingPlan}
                  component="legend"
                >Are you on a quarterly plan, or do you want to be on a quarterly plan?</FormLabel>
                <ErrorMessage name="billingPlan">
                  {(msg: any) => <FormHelperText error>{msg}</FormHelperText>}
                </ErrorMessage>
                <RadioGroup
                  className={classes.group}
                  aria-label="billingPlan"
                  name="billingPlan"
                  value={values.billingPlan}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <FormControlLabel control={<Radio />} label="Yes" value="quarterly" />
                  <FormControlLabel control={<Radio />} label="No" value="per-ad" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          
          <Collapse in={isRechargableOrg(values.orgType)} timeout={800}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Grow
                  in={isRechargableOrg(values.orgType)}
                  style={{
                    transformOrigin: '0 center'
                  }}
                  timeout={800}
                >
                  <FormControl fullWidth className={classes.formControl}>
                    <TextField
                      name="rechargeNum"
                      value={values.rechargeNum || '1811.'}
                      label="Recharge number"
                      helperText="List your department's CCOA recharge string using 770001 for the account segment. e.g. 1811.XXXXX.XXXXXXX.770001.XX.???.???????.??????.?????"
                      variant="outlined"
                      error={Boolean(errors.rechargeNum) && touched.rechargeNum}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <ErrorMessage name="rechargeNum">
                      {(msg: any) => <FormHelperText error>{msg}</FormHelperText>}
                    </ErrorMessage>
                  </FormControl>
                </Grow>
              </Grid>
            </Grid>
          </Collapse>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth className={classes.formControl}>
                <TextField
                  name="adName"
                  value={values.adName}
                  label="Ad name"
                  error={Boolean(errors.adName) && touched.adName}
                  variant="outlined"
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <ErrorMessage name="adName">
                  {(msg: any) => <FormHelperText error>{msg}</FormHelperText>}
                </ErrorMessage>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth className={classes.formControl}>
                <FormikDatePicker
                  name="firstDate"
                  autoOk={true}
                  value={values.firstDate}
                  label="First day"
                  format="MM/dd/yyyy"
                  InputLabelProps={{shrink: true}}
                  error={Boolean(errors.firstDate) && touched.firstDate}
                  inputVariant="outlined"
                />
                <ErrorMessage name="firstDate">
                  {(msg: any) => <FormHelperText error>{msg}</FormHelperText>}
                </ErrorMessage>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth className={classes.formControl}>
                <FormikDatePicker
                  name="lastDate"
                  autoOk={true}
                  value={values.lastDate}
                  label="Last day"
                  format="MM/dd/yyyy"
                  InputLabelProps={{shrink: true}}
                  error={Boolean(errors.lastDate) && touched.lastDate}
                  inputVariant="outlined"
                />
                <ErrorMessage name="lastDate">
                  {(msg: any) => <FormHelperText error>{msg}</FormHelperText>}
                </ErrorMessage>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FieldArray name="locations">
                {() => {
                  return (
                    <>
                      <FormControl className={classes.formControl}>
                        <FormLabel
                          error={Boolean(errors.locations) && Boolean(touched.locations)}
                        >Locations</FormLabel>
                        <FormHelperText error={Boolean(errors.locations)}>Select at least one location</FormHelperText>
                        <FormGroup>
                          <FormControlLabel
                            label="Residence Halls"
                            control={
                              <Checkbox
                                name="locations"
                                value="Residence Halls"
                                checked={R.includes('Residence Halls', values.locations)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            }
                          />
                          <FormControlLabel
                            label="Dining Commons"
                            control={
                              <Checkbox
                                name="locations"
                                value="Dining Commons"
                                checked={R.includes('Dining Commons', values.locations)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            }
                          />
                          <FormControlLabel
                            label="San Clemente"
                            control={
                              <Checkbox
                                name="locations"
                                value="San Clemente"
                                checked={R.includes('San Clemente', values.locations)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            }
                          />
                          <FormControlLabel
                            label="Loma Pelona"
                            control={
                              <Checkbox
                                name="locations"
                                value="Loma Pelona"
                                checked={R.includes('Loma Pelona', values.locations)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            }
                          />
                          <FormControlLabel
                            label="Family Student Housing"
                            control={
                              <Checkbox
                                name="locations"
                                value="Family Student Housing"
                                checked={R.includes('Family Student Housing', values.locations)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            }
                          />

                          <FormControlLabel
                            label="Undergraduate Apartments"
                            control={
                              <Checkbox
                                name="locations"
                                value="Undergraduate Apartments"
                                checked={R.includes('Undergraduate Apartments', values.locations)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            }
                          />
                        </FormGroup>
                      </FormControl>
                    </>
                  );
                }}
              </FieldArray>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth className={classes.formControl}>
                <TextField
                  name="slideCount"
                  value={values.slideCount}
                  label="Number of slides"
                  type="number"
                  inputProps={{min: '1'}}
                  error={Boolean(errors.slideCount) && touched.slideCount}
                  variant="outlined"
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <ErrorMessage name="slideCount">
                  {(msg: any) => <FormHelperText error>{msg}</FormHelperText>}
                </ErrorMessage>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth className={classes.formControl}>
                <TextField
                  multiline
                  name="comments"
                  value={values.comments}
                  label="Comments"
                  variant="outlined"
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FormControl>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <div className={classes.buttons}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                className={classes.button}
              >Save and continue</Button>
            </div>
          </Grid>
        </Form>
      }
    </Formik>
  );
};

export default AdDetailsForm;

export type AdRequestFormFields = {
  adName: string,
  contactPhoneNum: string,
  comments?: string,
  organization: string,
  organizationType: string,
  rechargeNumber?: string,
  billingPlan: string,
  slideCount: number,
  locations: DigiknowLocation[],
  firstDate: string,
  lastDate: string
}

type AdDetailsFormProps = {
  // TODO: rename -> onSubmit
  setAdDetails: (_: AdRequestFormFields) => void,
  proffer: AdRequestProffer
}
